import { edgeFunctionUrl, isSupabaseConfigured } from './supabaseClient';
import { loadConfig } from './configService';
import { getLang } from '../i18n';
import { logEvent } from './debugLog';

/**
 * 🔤 Sign translation (snapshot): grab a frame from the AR canvas, send it
 * to the `ocr-translate` Edge Function (Claude vision — OCR + translation
 * in one call, venue glossary injected server-side), get back the original
 * text + translation in the visitor's language.
 *
 * All gating is server-side too (feature flag, enabled languages, daily
 * cost cap) — the client treats 'cap' as a neutral "unavailable" state and
 * NEVER shows cost details to visitors (owner spec).
 *
 * ⚠️ Platform: iOS camera mode composites the camera feed into the canvas,
 * so frames contain the real scene. Android WebXR canvases have NO camera
 * pixels (system-composited) — same limitation as auto-capture; the HUD
 * button is not offered there.
 */

export interface TranslateResult {
  original: string;
  translated: string;
  sourceLang: string;
}

export type TranslateFailKind = 'cap' | 'unreadable' | 'blank' | 'error';

export class TranslateFail extends Error {
  constructor(readonly kind: TranslateFailKind, detail?: string) {
    super(detail ?? kind);
  }
}

/** Downscale the AR canvas to ≤maxW and return {base64 jpeg (no data: prefix), 2D context+size for sampling}. */
function drawDownscaled(canvas: HTMLCanvasElement, maxW: number, quality: number) {
  const scale = Math.min(1, maxW / Math.max(1, canvas.width));
  const w = Math.max(1, Math.round(canvas.width * scale));
  const h = Math.max(1, Math.round(canvas.height * scale));
  const off = document.createElement('canvas');
  off.width = w;
  off.height = h;
  const ctx = off.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new TranslateFail('error', 'canvas 2d context unavailable');
  ctx.drawImage(canvas, 0, 0, w, h);
  const dataUrl = off.toDataURL('image/jpeg', quality);
  return { b64: dataUrl.slice(dataUrl.indexOf(',') + 1), ctx, w, h };
}

/** Downscale the AR canvas to ≤maxW and return base64 jpeg (no data: prefix). */
export function grabFrameJpeg(canvas: HTMLCanvasElement, maxW = 1280, quality = 0.8): string {
  return drawDownscaled(canvas, maxW, quality).b64;
}

/**
 * Samples a grid of pixels across a drawn frame and flags it as BLANK when
 * every sample is near-black — the signature of reading a WebGL canvas
 * whose drawing buffer had already been cleared by the time we read it
 * (`preserveDrawingBuffer` isn't set on the main AR renderer; a tap-driven
 * capture like this one runs asynchronously relative to the render loop,
 * so on some devices — iOS Safari WebGL especially — the buffer can be
 * empty by the time `drawImage` reads it, even though the live preview the
 * visitor SEES looks completely normal). A real photographed sign always
 * has variance (paper texture, lighting, the sign itself) — genuinely
 * near-uniform near-black across the whole frame essentially never happens
 * for a real camera capture, so this is a high-confidence, low-false-positive
 * check, not a guess.
 */
function isBlankFrame(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
  const cols = 5;
  const rows = 5;
  let sum = 0;
  let n = 0;
  for (let r = 1; r < rows; r++) {
    for (let c = 1; c < cols; c++) {
      const x = Math.min(w - 1, Math.round((c / cols) * w));
      const y = Math.min(h - 1, Math.round((r / rows) * h));
      const px = ctx.getImageData(x, y, 1, 1).data;
      sum += px[0] + px[1] + px[2];
      n++;
    }
  }
  return n > 0 && sum / n < 4; // avg per-sample RGB sum near 0 = essentially pure black everywhere
}

/**
 * Grabs a frame, retrying up to `attempts` times (short delay between, via
 * requestAnimationFrame so each retry lines up with a fresh paint) if the
 * frame reads as blank (see isBlankFrame). Throws TranslateFail('blank')
 * if every attempt comes back blank — cheaper and more actionable than
 * sending a known-garbage frame to the paid vision call, which would just
 * come back 'unreadable' anyway.
 */
export async function grabFrameJpegRetrying(
  canvas: HTMLCanvasElement,
  opts: { maxW?: number; quality?: number; attempts?: number } = {},
): Promise<string> {
  const { maxW = 1280, quality = 0.8, attempts = 3 } = opts;
  for (let i = 0; i < attempts; i++) {
    if (i > 0) {
      // Two paint ticks' worth of delay — gives the app's own render loop
      // more chances to have just drawn a fresh frame before we read again.
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    }
    const { b64, ctx, w, h } = drawDownscaled(canvas, maxW, quality);
    if (!isBlankFrame(ctx, w, h)) {
      if (i > 0) logEvent('session', `translate: frame ok on attempt ${i + 1} (blank on ${i})`);
      return b64;
    }
    logEvent('session', `translate: blank frame on attempt ${i + 1}/${attempts}`);
  }
  throw new TranslateFail('blank', `frame still blank after ${attempts} attempts`);
}

/** Random per-page-load id — mirrors analyticsService's session grouping. */
const sessionId = Math.random().toString(36).slice(2, 10);

export async function translateSign(imageB64: string): Promise<TranslateResult> {
  if (!isSupabaseConfigured()) throw new TranslateFail('error', 'not configured');
  let resp: Response;
  try {
    resp = await fetch(edgeFunctionUrl('ocr-translate'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mapCode: loadConfig().code,
        image: imageB64,
        targetLang: getLang(),
        session: sessionId,
      }),
    });
  } catch (err) {
    logEvent('session', `translate: network fail — ${err instanceof Error ? err.message : String(err)}`);
    throw new TranslateFail('error', 'network');
  }
  const data = (await resp.json().catch(() => ({}))) as Partial<TranslateResult> & { error?: string };
  if (resp.status === 429 || data.error === 'cap') {
    logEvent('session', 'translate: venue daily cap reached');
    throw new TranslateFail('cap');
  }
  if (data.error === 'unreadable') {
    logEvent('session', 'translate: unreadable sign');
    throw new TranslateFail('unreadable');
  }
  if (!resp.ok || !data.original || !data.translated) {
    logEvent('session', `translate FAILED: ${data.error ?? `HTTP ${resp.status}`}`);
    throw new TranslateFail('error', data.error ?? `HTTP ${resp.status}`);
  }
  logEvent('session', `translate ok: ${data.sourceLang ?? '??'} → ${getLang()} (${data.original.length} chars)`);
  return {
    original: data.original,
    translated: data.translated,
    sourceLang: data.sourceLang ?? '??',
  };
}
